import { useCallback, useEffect, useRef, useState } from 'react';
import api from './../utils/api';
import { getPendingPayment, clearPendingPayment } from './../utils/pendingPayment';

/**
 * สถานะของการจองที่ค้างรอชำระเงินอยู่ (ถ้ามี)
 *
 * ตัวชี้เก็บไว้ใน localStorage ตอนสร้างการจอง แต่เชื่อมันเดี่ยวๆ ไม่ได้ -- ห้องอาจถูกปล่อยคืน
 * ไปแล้ว หรือลูกค้าจ่ายจบไปแล้วจากอีกแท็บ จึงต้องถามเซิร์ฟเวอร์ทุกครั้งก่อนเอาไปแสดง
 *
 * ใช้ร่วมกันระหว่างหน้าตะกร้ากับหน้ารายการห้อง -- ที่ผ่านมาทางกลับไปหน้าชำระเงินมีอยู่
 * ที่หน้าตะกร้าที่เดียว ลูกค้าที่จองห้องเดี่ยว (ซึ่งไม่ผ่านตะกร้าเลย) แล้วกดออกจากหน้าจ่ายเงิน
 * จึงหาทางกลับไม่เจอ และเห็นห้องที่ตัวเองกันไว้ขึ้นว่า "ไม่ว่าง"
 */
const keyOf = (type) => (type === 'cart' ? 'booking_cart_id' : (type === 'main' ? 'booking_main_id' : 'booking_id'));

const usePendingHold = ({ onReleased } = {}) => {
  const [pending, setPending] = useState(() => getPendingPayment());
  const [hold, setHold] = useState(null);
  const [checked, setChecked] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const timerRef = useRef(null);
  const expiredRef = useRef(null);

  const forget = useCallback(() => {
    clearPendingPayment();
    setPending(null);
    setHold(null);
  }, []);

  const check = useCallback((onGone) => {
    if (!pending?.id) { setChecked(true); return; }

    api.request('omise-payments/hold', (res, success) => {
      setChecked(true);
      if (!success || !res) return;

      // จ่ายจบแล้ว หรือห้องถูกปล่อยคืนไปแล้ว -- ไม่ต้องชวนให้กดกลับไปเจอหน้าเปล่า
      if (res.paid || !res.alive || !res.found) {
        forget();
        if (typeof onGone === 'function') onGone();
        return;
      }
      setHold(res);
    }, { [keyOf(pending.type)]: pending.id }, 'GET', { showError: false });
  }, [pending, forget]);

  useEffect(() => { check(); }, [check]);

  // นับถอยหลังในเครื่อง ไม่ต้องถามเซิร์ฟเวอร์ทุกวินาที
  useEffect(() => {
    if (hold?.seconds_left === undefined || hold?.seconds_left === null) return undefined;

    timerRef.current = setInterval(() => {
      setHold((prev) => {
        if (!prev || prev.seconds_left === null) return prev;
        const next = prev.seconds_left - 1;
        if (next <= 0) {
          clearInterval(timerRef.current);
          // นาฬิกาเป็นแค่ตัวนับในเครื่อง ถึง 0 ไม่ได้แปลว่าห้องถูกปล่อยแล้วจริง
          // ต้องถามเซิร์ฟเวอร์ก่อน -- ไม่งั้นแถบจะค้างจนกว่าลูกค้าจะรีเฟรชเอง
          // (งานปล่อยห้องทำงานทุก 1 นาที จึงเผื่อเวลาให้มันทำงานก่อนค่อยถาม)
          expiredRef.current = setTimeout(() => check(onReleased), 3000);
          return { ...prev, seconds_left: 0 };
        }
        return { ...prev, seconds_left: next };
      });
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(expiredRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold?.expires_at]);

  /** ปล่อยห้องคืนทันทีตามคำขอของลูกค้า ไม่ต้องรอ cron */
  const release = useCallback((onDone) => {
    if (!pending?.id || releasing) return;
    setReleasing(true);
    api.request('omise-payments/hold/release', () => {
      setReleasing(false);
      forget();
      if (typeof onDone === 'function') onDone();
    }, { [keyOf(pending.type)]: pending.id }, 'POST', { showError: false });
  }, [pending, releasing, forget]);

  const heldRooms = hold?.held_rooms || { room_ids: [], room_flexible_ids: [], rooms: [] };

  return {
    pending: hold ? pending : null,
    // ตัวชี้ดิบใน localStorage -- ยังไม่ได้ยืนยันกับเซิร์ฟเวอร์ ห้ามเอาไปแสดงเป็นเรื่องจริง
    // มีไว้ให้ปุ่ม "จอง" กั้นไว้ก่อนระหว่างรอคำตอบ ไม่งั้นคนที่กดเร็วจะเล็ดลอดไปได้
    pointer: pending,
    hold,
    checked,
    releasing,
    release,
    forget,
    check,
    heldRoomIds: heldRooms.room_ids || [],
    heldFlexibleIds: heldRooms.room_flexible_ids || [],
    // ชื่อห้องกับวันเข้าพัก -- แถบต้องบอกได้ว่าที่ค้างอยู่คือห้องไหน ไม่ใช่มีแค่เลขที่จอง
    heldRooms: heldRooms.rooms || [],
    stay: hold?.stay || null,
  };
};

export default usePendingHold;
